import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { WhiteLabelService } from '@flexyoo/common';
import { EmployeeSelectedBenefit } from '@flexyoo/employee/models';
import { employeeSelectedBenefitsSelectors } from '@flexyoo/employee/store';
import { FlexyooNumberPipe } from '@flexyoo/shared/pipes';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { EmployeeFinancingPlanBadgeComponent } from '../employee-financing-plan-badge/employee-financing-plan-badge.component';

type IPriorYearPaymentsKind = 'reserved' | 'paid';

type IPriorYearPaymentsGroup = {
  readonly kind: IPriorYearPaymentsKind;
  readonly icon: string;
  readonly statusKey: string;
  readonly benefits: readonly EmployeeSelectedBenefit[];
  readonly statusColor: string;
  readonly countBackgroundColor: string;
  readonly countTextColor: string;
};

@Component({
  selector: 'flexyoo-employee-basket-prior-year-payments',
  templateUrl: './employee-basket-prior-year-payments.component.html',
  styleUrl: './employee-basket-prior-year-payments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UpperCasePipe,
    TranslateModule,
    MatIconModule,
    MatExpansionModule,
    FlexyooNumberPipe,
    EmployeeFinancingPlanBadgeComponent,
  ],
})
export class EmployeeBasketPriorYearPaymentsComponent {
  private readonly store = inject(Store);
  private readonly wls = inject(WhiteLabelService);

  private readonly reservedBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectPriorYearReservedBenefits),
    { initialValue: [] }
  );
  private readonly paidBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectPriorYearPaidBenefits),
    { initialValue: [] }
  );

  protected readonly groups = computed((): readonly IPriorYearPaymentsGroup[] => {
    const groups: IPriorYearPaymentsGroup[] = [];
    const reservedBenefits = this.reservedBenefits();
    if (reservedBenefits.length > 0) {
      groups.push({
        kind: 'reserved',
        icon: 'fo-hourglasses',
        statusKey: 'employee.basket.prior-year-status.reserved',
        benefits: reservedBenefits,
        statusColor: this.wls.warnColor,
        countBackgroundColor: this.wls.warnColor,
        countTextColor: this.wls.warnContrastingColor,
      });
    }

    const paidBenefits = this.paidBenefits();
    if (paidBenefits.length > 0) {
      groups.push({
        kind: 'paid',
        icon: 'fo-budgets',
        statusKey: 'employee.basket.prior-year-status.paid',
        benefits: paidBenefits,
        statusColor: this.wls.primaryColor,
        countBackgroundColor: this.wls.primaryColor,
        countTextColor: this.wls.primaryContrastingColor,
      });
    }

    return groups;
  });
}
